import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Sparkles,
  Loader2,
  Printer,
  Save,
  ChevronDown,
  ChevronUp,
  Search,
  Beaker,
  BookOpen,
  Puzzle,
  ClipboardCheck,
  Clock,
  AlertTriangle,
  Users,
  Lightbulb,
  FileText,
  Share2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = '';

const phaseConfig = {
  engage: {
    label: 'Engage',
    icon: Sparkles,
    color: 'amber',
    bgClass: 'bg-amber-50 border-amber-200',
    headerClass: 'bg-amber-100 text-amber-900',
    badgeClass: 'bg-amber-500',
    description: 'Hook students\' interest and activate prior knowledge',
  },
  explore: {
    label: 'Explore',
    icon: Search,
    color: 'blue',
    bgClass: 'bg-blue-50 border-blue-200',
    headerClass: 'bg-blue-100 text-blue-900',
    badgeClass: 'bg-blue-500',
    description: 'Hands-on investigation and discovery',
  },
  explain: {
    label: 'Explain',
    icon: BookOpen,
    color: 'green',
    bgClass: 'bg-green-50 border-green-200',
    headerClass: 'bg-green-100 text-green-900',
    badgeClass: 'bg-green-500',
    description: 'Formal concepts, definitions, and formulas',
  },
  elaborate: {
    label: 'Elaborate',
    icon: Puzzle,
    color: 'purple',
    bgClass: 'bg-purple-50 border-purple-200',
    headerClass: 'bg-purple-100 text-purple-900',
    badgeClass: 'bg-purple-500',
    description: 'Apply and extend knowledge to new contexts',
  },
  evaluate: {
    label: 'Evaluate',
    icon: ClipboardCheck,
    color: 'red',
    bgClass: 'bg-red-50 border-red-200',
    headerClass: 'bg-red-100 text-red-900',
    badgeClass: 'bg-red-500',
    description: 'Assess understanding and reflect',
  },
};

const FiveELessonGenerator = ({ defaultYear = 11, allowedYears = [11, 12, 13] }) => {
  const [topic, setTopic] = useState('');
  const [selectedYear, setSelectedYear] = useState(defaultYear.toString());
  const [duration, setDuration] = useState('45');
  const [strand, setStrand] = useState('');
  const [learningOutcomes, setLearningOutcomes] = useState('');
  const [loading, setLoading] = useState(false);
  const [lesson, setLesson] = useState(null);
  const [expandedPhases, setExpandedPhases] = useState({
    engage: true, explore: true, explain: true, elaborate: true, evaluate: true,
  });
  const [sharingToAcademic, setSharingToAcademic] = useState(false);

  const togglePhase = (phase) => {
    setExpandedPhases(prev => ({ ...prev, [phase]: !prev[phase] }));
  };

  const shareToAcademic = async () => {
    if (!lesson) return;
    const userStr = localStorage.getItem('currentUser') || localStorage.getItem('user');
    const currentUser = userStr ? JSON.parse(userStr) : null;
    if (!currentUser?.id) {
      toast.error('Please log in to share');
      return;
    }
    if (!currentUser.school_id) {
      toast.error('School ID not found. Please log out and log in again.');
      return;
    }
    setSharingToAcademic(true);
    try {
      const payload = {
        teacher_id: currentUser.id,
        teacher_name: currentUser.name,
        school_id: currentUser.school_id,
        submission_type: 'lesson_plan',
        title: lesson.title || `5E Lesson Plan: Year ${selectedYear} Physics - ${topic}`,
        subject: 'Physics',
        year_level: parseInt(selectedYear),
        term: 1,
        content: lesson
      };
      const response = await fetch(`${API_URL}/api/academic/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to submit');
      toast.success('5E Lesson Plan submitted to Academic Principal for approval!');
    } catch (error) {
      toast.error('Failed to submit to Academic. Please try again.');
    } finally {
      setSharingToAcademic(false);
    }
  };

  const generate5ELesson = async () => {
    if (!topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }
    setLoading(true);
    setLesson(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('authToken');
      const body = {
        topic: topic.trim(),
        subject: 'physics',
        year_level: parseInt(selectedYear),
        duration: parseInt(duration),
        school_type: 'secondary',
      };
      if (strand.trim()) body.strand = strand.trim();
      if (learningOutcomes.trim()) {
        body.learning_outcomes = learningOutcomes.split('\n').filter(l => l.trim());
      }

      const response = await fetch(`${API_URL}/api/ai-lessons/generate-5e`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (data.success) {
        setLesson(data.lesson);
        toast.success('5E Lesson Plan generated!');
      } else {
        toast.error(data.detail || 'Failed to generate 5E lesson');
      }
    } catch (error) {
      toast.error('Failed to generate 5E lesson plan');
    } finally {
      setLoading(false);
    }
  };

  const print5ELesson = () => {
    if (!lesson) return;
    const printWindow = window.open('', '_blank');
    const phases = lesson.phases || {};

    const phaseHTML = (key, config) => {
      const p = phases[key];
      if (!p) return '';
      return `
        <div class="phase">
          <div class="phase-header phase-${key}">
            <span class="phase-num">${Object.keys(phaseConfig).indexOf(key) + 1}</span>
            ${config.label.toUpperCase()}
            <span class="phase-time">${p.duration || ''}</span>
          </div>
          <div class="phase-body">
            <div class="phase-row">
              <div class="phase-col">
                <strong>Teacher Activity:</strong>
                <p>${p.teacher_activity || p.formative_assessment || ''}</p>
              </div>
              <div class="phase-col">
                <strong>Student Activity:</strong>
                <p>${p.student_activity || p.student_reflection || ''}</p>
              </div>
            </div>
            ${p.key_questions ? `<div class="key-questions"><strong>Key Questions:</strong><ul>${p.key_questions.map(q => `<li>${q}</li>`).join('')}</ul></div>` : ''}
            ${p.key_concepts ? `<div class="key-questions"><strong>Key Concepts:</strong><ul>${p.key_concepts.map(c => `<li>${c}</li>`).join('')}</ul></div>` : ''}
            ${p.vocabulary ? `<div class="key-questions"><strong>Vocabulary:</strong> ${p.vocabulary.join(', ')}</div>` : ''}
            ${p.extension_tasks ? `<div class="key-questions"><strong>Extension:</strong><ul>${p.extension_tasks.map(t => `<li>${t}</li>`).join('')}</ul></div>` : ''}
            ${p.real_world_connection ? `<div class="key-questions"><strong>Real-World Connection:</strong> ${p.real_world_connection}</div>` : ''}
            ${p.success_criteria ? `<div class="key-questions"><strong>Success Criteria:</strong><ul>${p.success_criteria.map(c => `<li>${c}</li>`).join('')}</ul></div>` : ''}
            ${p.safety_notes ? `<div class="safety"><strong>Safety:</strong> ${p.safety_notes}</div>` : ''}
            ${p.resources ? `<div class="key-questions"><strong>Resources:</strong> ${p.resources}</div>` : ''}
          </div>
        </div>
      `;
    };

    printWindow.document.write(`<!DOCTYPE html><html><head>
      <title>5E Lesson Plan - ${lesson.title || topic}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; padding: 20px; line-height: 1.5; font-size: 11pt; }
        .header { text-align: center; border-bottom: 3px solid #333; padding-bottom: 15px; margin-bottom: 15px; }
        .header h1 { font-size: 16pt; margin-bottom: 5px; }
        .header h2 { font-size: 13pt; color: #555; }
        .meta { display: flex; justify-content: space-between; margin: 10px 0; font-size: 10pt; flex-wrap: wrap; gap: 8px; }
        .meta span { background: #f0f0f0; padding: 3px 10px; border-radius: 4px; }
        .objectives { background: #f8f9fa; padding: 10px; border-radius: 6px; margin: 10px 0; }
        .objectives h3 { margin-bottom: 5px; }
        .objectives ul { margin-left: 18px; }
        .materials { margin: 10px 0; padding: 8px; background: #fff8e1; border-radius: 6px; }
        .phase { margin: 12px 0; border: 1px solid #ddd; border-radius: 8px; overflow: hidden; page-break-inside: avoid; }
        .phase-header { padding: 8px 12px; font-weight: bold; font-size: 12pt; display: flex; align-items: center; gap: 10px; }
        .phase-num { width: 24px; height: 24px; border-radius: 50%; background: rgba(0,0,0,0.15); display: inline-flex; align-items: center; justify-content: center; font-size: 10pt; }
        .phase-time { margin-left: auto; font-size: 10pt; font-weight: normal; opacity: 0.8; }
        .phase-engage { background: #fff3cd; color: #856404; }
        .phase-explore { background: #cce5ff; color: #004085; }
        .phase-explain { background: #d4edda; color: #155724; }
        .phase-elaborate { background: #e8d5f5; color: #4a148c; }
        .phase-evaluate { background: #f8d7da; color: #721c24; }
        .phase-body { padding: 10px; }
        .phase-row { display: flex; gap: 15px; }
        .phase-col { flex: 1; }
        .key-questions { margin-top: 8px; font-size: 10pt; }
        .key-questions ul { margin-left: 18px; }
        .safety { margin-top: 8px; padding: 5px 8px; background: #fff3cd; border-radius: 4px; font-size: 10pt; }
        .diff { margin: 12px 0; display: flex; gap: 10px; }
        .diff > div { flex: 1; padding: 8px; border-radius: 6px; font-size: 10pt; }
        .diff-struggle { background: #e3f2fd; }
        .diff-advance { background: #fce4ec; }
        .footer { text-align: center; margin-top: 15px; padding: 10px; border-top: 1px solid #ddd; font-size: 9pt; color: #999; }
        @media print { body { padding: 10mm; } .phase { page-break-inside: avoid; } }
      </style>
    </head><body>
      <div class="header">
        <h1>${lesson.title || `5E Lesson Plan: ${topic}`}</h1>
        <h2>Physics | Year ${selectedYear} | ${lesson.duration || duration + ' minutes'}</h2>
      </div>
      <div class="meta">
        <span><strong>Topic:</strong> ${lesson.topic || topic}</span>
        ${lesson.prior_knowledge ? `<span><strong>Prior Knowledge:</strong> ${lesson.prior_knowledge}</span>` : ''}
      </div>
      <div class="objectives">
        <h3>Learning Objectives:</h3>
        <ul>${(lesson.objectives || []).map(o => `<li>${o}</li>`).join('')}</ul>
      </div>
      <div class="materials">
        <strong>Materials:</strong> ${Array.isArray(lesson.materials) ? lesson.materials.join(', ') : (lesson.materials || 'N/A')}
      </div>
      ${Object.entries(phaseConfig).map(([key, config]) => phaseHTML(key, config)).join('')}
      ${lesson.differentiation ? `
        <div class="diff">
          <div class="diff-struggle"><strong>Struggling Learners:</strong> ${lesson.differentiation.struggling || ''}</div>
          <div class="diff-advance"><strong>Advanced Learners:</strong> ${lesson.differentiation.advanced || ''}</div>
        </div>
      ` : ''}
      ${lesson.homework ? `<div class="materials"><strong>Homework:</strong> ${lesson.homework}</div>` : ''}
      ${lesson.teacher_notes ? `<div class="materials"><strong>Teacher Notes:</strong> ${lesson.teacher_notes}</div>` : ''}
      <div class="footer">5E Model Lesson Plan | Generated ${new Date().toLocaleDateString()}</div>
    </body></html>`);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const renderPhase = (phaseKey) => {
    const config = phaseConfig[phaseKey];
    const phase = lesson?.phases?.[phaseKey];
    if (!phase) return null;
    const Icon = config.icon;
    const isExpanded = expandedPhases[phaseKey];

    return (
      <div key={phaseKey} className={`border rounded-lg overflow-hidden ${config.bgClass}`} data-testid={`5e-phase-${phaseKey}`}>
        <button
          onClick={() => togglePhase(phaseKey)}
          className={`w-full flex items-center gap-3 px-4 py-3 ${config.headerClass} font-semibold text-left`}
        >
          <div className={`w-7 h-7 rounded-full ${config.badgeClass} text-white flex items-center justify-center text-sm`}>
            {Object.keys(phaseConfig).indexOf(phaseKey) + 1}
          </div>
          <Icon className="w-5 h-5" />
          <span className="flex-1">{config.label}</span>
          {phase.duration && (
            <Badge variant="outline" className="mr-2">
              <Clock className="w-3 h-3 mr-1" />{phase.duration}
            </Badge>
          )}
          {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {isExpanded && (
          <div className="p-4 space-y-3">
            <p className="text-xs italic opacity-60">{config.description}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {phase.teacher_activity && (
                <div className="bg-white/70 p-3 rounded-lg">
                  <span className="font-medium text-sm flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Teacher Activity</span>
                  <p className="text-sm mt-1 text-gray-700">{phase.teacher_activity}</p>
                </div>
              )}
              {phase.student_activity && (
                <div className="bg-white/70 p-3 rounded-lg">
                  <span className="font-medium text-sm flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Student Activity</span>
                  <p className="text-sm mt-1 text-gray-700">{phase.student_activity}</p>
                </div>
              )}
              {phase.formative_assessment && (
                <div className="bg-white/70 p-3 rounded-lg">
                  <span className="font-medium text-sm flex items-center gap-1"><ClipboardCheck className="w-3.5 h-3.5" /> Formative Assessment</span>
                  <p className="text-sm mt-1 text-gray-700">{phase.formative_assessment}</p>
                </div>
              )}
              {phase.student_reflection && (
                <div className="bg-white/70 p-3 rounded-lg">
                  <span className="font-medium text-sm flex items-center gap-1"><Lightbulb className="w-3.5 h-3.5" /> Student Reflection</span>
                  <p className="text-sm mt-1 text-gray-700">{phase.student_reflection}</p>
                </div>
              )}
            </div>

            {phase.key_questions?.length > 0 && (
              <div className="bg-white/70 p-3 rounded-lg">
                <span className="font-medium text-sm">Key Questions:</span>
                <ul className="list-disc list-inside text-sm text-gray-700 mt-1">
                  {phase.key_questions.map((q, i) => <li key={i}>{q}</li>)}
                </ul>
              </div>
            )}

            {phase.key_concepts?.length > 0 && (
              <div className="bg-white/70 p-3 rounded-lg">
                <span className="font-medium text-sm">Key Concepts & Formulas:</span>
                <ul className="list-disc list-inside text-sm text-gray-700 mt-1">
                  {phase.key_concepts.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}

            {phase.vocabulary?.length > 0 && (
              <div className="bg-white/70 p-3 rounded-lg">
                <span className="font-medium text-sm">Vocabulary:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {phase.vocabulary.map((v, i) => (
                    <Badge key={i} variant="outline" className="text-xs">{v}</Badge>
                  ))}
                </div>
              </div>
            )}

            {phase.extension_tasks?.length > 0 && (
              <div className="bg-white/70 p-3 rounded-lg">
                <span className="font-medium text-sm">Extension Tasks:</span>
                <ul className="list-disc list-inside text-sm text-gray-700 mt-1">
                  {phase.extension_tasks.map((t, i) => <li key={i}>{t}</li>)}
                </ul>
              </div>
            )}

            {phase.real_world_connection && (
              <div className="bg-white/70 p-3 rounded-lg">
                <span className="font-medium text-sm">Real-World Connection:</span>
                <p className="text-sm mt-1 text-gray-700">{phase.real_world_connection}</p>
              </div>
            )}

            {phase.success_criteria?.length > 0 && (
              <div className="bg-white/70 p-3 rounded-lg">
                <span className="font-medium text-sm">Success Criteria:</span>
                <ul className="list-disc list-inside text-sm text-gray-700 mt-1">
                  {phase.success_criteria.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>
            )}

            {phase.safety_notes && (
              <div className="bg-yellow-50 border border-yellow-200 p-3 rounded-lg flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5 shrink-0" />
                <div>
                  <span className="font-medium text-sm text-yellow-800">Safety Notes:</span>
                  <p className="text-sm text-yellow-700">{phase.safety_notes}</p>
                </div>
              </div>
            )}

            {phase.summative_ideas && (
              <div className="bg-white/70 p-3 rounded-lg">
                <span className="font-medium text-sm">Summative Assessment Ideas:</span>
                <p className="text-sm mt-1 text-gray-700">{phase.summative_ideas}</p>
              </div>
            )}

            {phase.resources && (
              <p className="text-xs text-gray-500">Resources: {phase.resources}</p>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="border-2 border-teal-200" data-testid="5e-lesson-generator">
      <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50 pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Beaker className="w-5 h-5 text-teal-600" />
          5E Model Lesson Plan Generator
          <Badge className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white ml-2 text-xs">
            Physics Only
          </Badge>
        </CardTitle>
        <p className="text-sm text-gray-500 mt-1">
          Generate structured lesson plans using the 5E Instructional Model: Engage, Explore, Explain, Elaborate, Evaluate
        </p>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        {/* Input Form */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="lg:col-span-2">
            <Label>Topic / Lesson Focus *</Label>
            <Input
              placeholder="e.g., Newton's Second Law, Ohm's Law, Projectile Motion"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="mt-1"
              data-testid="5e-topic-input"
            />
          </div>
          <div>
            <Label>Year Level</Label>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {allowedYears.map(y => {
                  const yVal = typeof y === 'string' ? parseInt(y.replace('Year ', '')) : y;
                  return <SelectItem key={yVal} value={yVal.toString()}>Year {yVal}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Duration</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({length: 28}, (_, i) => 15 + i * 5).map(min => (
                  <SelectItem key={min} value={String(min)}>{min} min</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label>Strand (optional)</Label>
            <Input
              placeholder="e.g., PHY2: Mechanics"
              value={strand}
              onChange={(e) => setStrand(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label>Specific Learning Outcomes (optional, one per line)</Label>
            <Textarea
              placeholder="e.g., State Newton's second law&#10;Calculate F = ma for given scenarios"
              value={learningOutcomes}
              onChange={(e) => setLearningOutcomes(e.target.value)}
              className="mt-1"
              rows={2}
            />
          </div>
        </div>

        {/* Generate Button */}
        <Button
          onClick={generate5ELesson}
          disabled={loading || !topic.trim()}
          className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
          data-testid="generate-5e-btn"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating 5E Lesson...</>
          ) : (
            <><Beaker className="w-4 h-4 mr-2" />Generate 5E Lesson Plan</>
          )}
        </Button>

        {/* Generated 5E Lesson */}
        {lesson && (
          <div className="space-y-4" data-testid="5e-lesson-result">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-lg text-teal-900">{lesson.title || `5E Lesson: ${topic}`}</h3>
                  <p className="text-sm text-teal-700 mt-1">
                    Year {selectedYear} Physics | {lesson.duration || duration + ' min'}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={shareToAcademic}
                    disabled={sharingToAcademic}
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                    data-testid="share-5e-to-academic-btn"
                  >
                    {sharingToAcademic ? (
                      <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Sharing...</>
                    ) : (
                      <><Share2 className="w-4 h-4 mr-1" />Share to Academic</>
                    )}
                  </Button>
                  <Button size="sm" variant="outline" onClick={print5ELesson} data-testid="print-5e-btn">
                    <Printer className="w-4 h-4 mr-1" />Print
                  </Button>
                </div>
              </div>

              {/* Objectives */}
              {lesson.objectives?.length > 0 && (
                <div className="mt-3">
                  <span className="font-medium text-sm text-teal-800">Learning Objectives:</span>
                  <ul className="list-disc list-inside text-sm text-gray-700 mt-1">
                    {lesson.objectives.map((obj, i) => <li key={i}>{obj}</li>)}
                  </ul>
                </div>
              )}

              {/* Materials */}
              {lesson.materials && (
                <div className="mt-2">
                  <span className="font-medium text-sm text-teal-800">Materials:</span>
                  <p className="text-sm text-gray-700 mt-0.5">
                    {Array.isArray(lesson.materials) ? lesson.materials.join(', ') : lesson.materials}
                  </p>
                </div>
              )}

              {/* Prior Knowledge */}
              {lesson.prior_knowledge && (
                <div className="mt-2">
                  <span className="font-medium text-sm text-teal-800">Prior Knowledge:</span>
                  <p className="text-sm text-gray-600 italic mt-0.5">{lesson.prior_knowledge}</p>
                </div>
              )}
            </div>

            {/* 5E Phases */}
            <div className="space-y-3">
              {Object.keys(phaseConfig).map(phaseKey => renderPhase(phaseKey))}
            </div>

            {/* Differentiation */}
            {lesson.differentiation && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {lesson.differentiation.struggling && (
                  <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
                    <span className="font-medium text-sm text-blue-800 flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" /> Support for Struggling Learners
                    </span>
                    <p className="text-sm text-gray-700 mt-1">{lesson.differentiation.struggling}</p>
                  </div>
                )}
                {lesson.differentiation.advanced && (
                  <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg">
                    <span className="font-medium text-sm text-orange-800 flex items-center gap-1">
                      <Lightbulb className="w-3.5 h-3.5" /> Extension for Advanced Learners
                    </span>
                    <p className="text-sm text-gray-700 mt-1">{lesson.differentiation.advanced}</p>
                  </div>
                )}
              </div>
            )}

            {/* Homework & Teacher Notes */}
            {(lesson.homework || lesson.teacher_notes) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {lesson.homework && (
                  <div className="bg-gray-50 border p-3 rounded-lg">
                    <span className="font-medium text-sm flex items-center gap-1">
                      <FileText className="w-3.5 h-3.5" /> Homework
                    </span>
                    <p className="text-sm text-gray-700 mt-1">{lesson.homework}</p>
                  </div>
                )}
                {lesson.teacher_notes && (
                  <div className="bg-gray-50 border p-3 rounded-lg">
                    <span className="font-medium text-sm flex items-center gap-1">
                      <Lightbulb className="w-3.5 h-3.5" /> Teacher Notes
                    </span>
                    <p className="text-sm text-gray-700 mt-1">{lesson.teacher_notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FiveELessonGenerator;
